<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header hris-page-header-row">
        <div>
          <span class="hris-page-eyebrow">Recruitment</span>
          <h1 class="hris-page-title">Applicant Tracking</h1>
          <p class="hris-page-subtitle">
            Manage vacancies, applicants, approvals, offers, and onboarding handoff in one workflow.
          </p>
        </div>
        <div class="ats-header-actions">
          <span class="hris-badge hris-badge-accent">{{ applications.length }} Applicants</span>
          <q-btn unelevated class="hris-q-primary" icon="fas fa-plus" label="New Vacancy"
            @click="openVacancyDialog(null)"></q-btn>
          <q-btn unelevated class="hris-q-outline" icon="fas fa-sync" label="Refresh" @click="refreshAll"></q-btn>
        </div>
      </div>

      <div class="row q-col-gutter-md q-mb-md">
        <div v-for="card in summaryCards" :key="card.label" class="col-12 col-sm-6 col-lg-3">
          <div class="hris-stat-card">
            <p class="hris-stat-label">{{ card.label }}</p>
            <p class="hris-stat-value">{{ card.value }}</p>
            <p class="hris-stat-meta"><i :class="card.icon" class="q-mr-xs"></i>Current pipeline count</p>
          </div>
        </div>
      </div>

      <div class="hris-panel" style="padding: 0; margin-bottom: 20px; overflow: hidden;">
        <q-tabs v-model="tab" active-color="green" indicator-color="green" align="left" inline-label dense>
          <q-tab name="vacancies" icon="fas fa-briefcase" label="Vacancies"></q-tab>
          <q-tab name="applications" icon="fas fa-users" label="Applicants"></q-tab>
          <q-tab name="approvals" icon="fas fa-check-double" label="Approvals"></q-tab>
          <q-tab name="offers" icon="fas fa-file-signature" label="Offers"></q-tab>
        </q-tabs>
      </div>

      <q-tab-panels v-model="tab" animated style="background: transparent" class="q-pa-none">
        <q-tab-panel name="vacancies" class="q-pa-none">
          <div class="hris-toolbar">
            <div>
              <div class="text-subtitle1 text-weight-bold">Vacancies</div>
              <div class="text-caption text-grey-7">Track job openings, ownership, and publish status.</div>
            </div>
            <div class="hris-toolbar-spacer"></div>
            <q-input v-model="vacancyFilter" dense outlined color="green" placeholder="Search vacancies..."
              style="min-width: 280px">
              <template v-slot:append><q-icon name="fas fa-search"></q-icon></template>
            </q-input>
          </div>
          <div class="hris-panel q-pa-none">
            <q-table :data="vacancies" :columns="vacancyColumns" row-key="id" :filter="vacancyFilter"
              :loading="loadingVacancies" separator="horizontal" flat>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="title" :props="props">
                    <div class="text-weight-bold">{{ props.row.title }}</div>
                    <div class="text-caption text-grey-7">{{ props.row.department || 'No department' }} - {{
                      props.row.site || 'No site' }}</div>
                  </q-td>
                  <q-td key="employment_type" :props="props">{{ props.row.employment_type }}</q-td>
                  <q-td key="headcount" :props="props">{{ props.row.headcount }}</q-td>
                  <q-td key="recruiter" :props="props">{{ employeeName(props.row.recruiter) }}</q-td>
                  <q-td key="status" :props="props">
                    <q-chip dense :color="vacancyStatusColor(props.row.status)" text-color="white">
                      {{ normalizeLabel(props.row.status) }}
                    </q-chip>
                  </q-td>
                  <q-td key="open_date" :props="props">{{ formatDate(props.row.open_date) }}</q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-btn flat round dense class="hris-q-ghost" icon="fas fa-edit"
                      @click="openVacancyDialog(props.row)">
                      <q-tooltip>Edit Vacancy</q-tooltip>
                    </q-btn>
                    <q-btn v-if="props.row.status !== 'published'" flat round dense class="hris-q-ghost"
                      icon="fas fa-bullhorn" @click="publishVacancy(props.row)">
                      <q-tooltip>Publish</q-tooltip>
                    </q-btn>
                    <q-btn v-if="props.row.status === 'published'" flat round dense class="hris-q-ghost"
                      icon="fas fa-lock" @click="closeVacancy(props.row)">
                      <q-tooltip>Close</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">
                  No vacancies yet.
                </div>
              </template>
            </q-table>
          </div>
        </q-tab-panel>

        <q-tab-panel name="applications" class="q-pa-none">
          <div class="hris-toolbar">
            <div>
              <div class="text-subtitle1 text-weight-bold">Applicants</div>
              <div class="text-caption text-grey-7">Search and move candidates through the pipeline.</div>
            </div>
            <div class="hris-toolbar-spacer"></div>
            <q-select v-model="applicationStatusFilter" :options="applicationStatuses" dense outlined color="green"
              clearable label="Status" style="min-width: 180px" @input="getApplications"></q-select>
            <q-input v-model="applicationSearch" dense outlined color="green" placeholder="Search applicants..."
              style="min-width: 240px" @keyup.enter="getApplications">
              <template v-slot:append><q-icon name="fas fa-search"></q-icon></template>
            </q-input>
            <q-btn unelevated class="hris-q-primary" icon="fas fa-filter" label="Apply"
              @click="getApplications"></q-btn>
          </div>
          <div class="hris-panel q-pa-none">
            <q-table :data="applications" :columns="applicationColumns" row-key="id" :loading="loadingApplications"
              separator="horizontal" flat>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="tracking_id" :props="props">{{ props.row.tracking_id }}</q-td>
                  <q-td key="name" :props="props">
                    <div class="text-weight-bold">{{ applicantName(props.row) }}</div>
                    <div class="text-caption text-grey-7">{{ props.row.email || 'No email' }}</div>
                  </q-td>
                  <q-td key="position" :props="props">{{ applicantPosition(props.row) }}</q-td>
                  <q-td key="application_status" :props="props">
                    <q-chip dense :color="applicationStatusColor(props.row.application_status)" text-color="white">
                      {{ normalizeLabel(props.row.application_status || 'applied') }}
                    </q-chip>
                  </q-td>
                  <q-td key="date_of_application" :props="props">{{ formatDate(props.row.date_of_application) }}</q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-btn flat round dense class="hris-q-ghost" icon="fas fa-id-card"
                      @click="openApplicationProfile(props.row)">
                      <q-tooltip>Applicant Profile</q-tooltip>
                    </q-btn>
                    <q-btn flat round dense class="hris-q-ghost" icon="fas fa-clipboard-check"
                      @click="openScreeningDialog(props.row)">
                      <q-tooltip>Screen Applicant</q-tooltip>
                    </q-btn>
                    <q-btn flat round dense class="hris-q-ghost" icon="fas fa-calendar-plus"
                      @click="openInterviewDialog(props.row)">
                      <q-tooltip>Schedule Interview</q-tooltip>
                    </q-btn>
                    <q-btn flat round dense class="hris-q-ghost" icon="fas fa-user-check"
                      @click="openApprovalDialog(props.row)">
                      <q-tooltip>Request Approval</q-tooltip>
                    </q-btn>
                    <q-btn flat round dense class="hris-q-ghost" icon="fas fa-file-signature"
                      @click="openOfferDialog(props.row)">
                      <q-tooltip>Create Offer</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">
                  No applicants found.
                </div>
              </template>
            </q-table>
          </div>
        </q-tab-panel>

        <q-tab-panel name="approvals" class="q-pa-none">
          <div class="hris-toolbar">
            <div>
              <div class="text-subtitle1 text-weight-bold">Approvals</div>
              <div class="text-caption text-grey-7">Review pending hiring approvals and action them quickly.</div>
            </div>
          </div>
          <div class="hris-panel q-pa-none">
            <q-table :data="approvals" :columns="approvalColumns" row-key="id" :loading="loadingApprovals"
              separator="horizontal" flat>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="applicant" :props="props">{{ props.row.application ? applicantName(props.row.application) :
                    'N/A' }}</q-td>
                  <q-td key="vacancy" :props="props">{{ props.row.vacancy ? props.row.vacancy.title : 'N/A' }}</q-td>
                  <q-td key="approval_level" :props="props">{{ props.row.approval_level }}</q-td>
                  <q-td key="requested_by" :props="props">{{ employeeName(props.row.requester) }}</q-td>
                  <q-td key="created_at" :props="props">{{ formatDate(props.row.created_at) }}</q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-btn flat round dense class="hris-q-ghost" icon="fas fa-check"
                      @click="actOnApproval(props.row, 'approve')">
                      <q-tooltip>Approve</q-tooltip>
                    </q-btn>
                    <q-btn flat round dense class="hris-q-ghost" icon="fas fa-times"
                      @click="actOnApproval(props.row, 'reject')">
                      <q-tooltip>Reject</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">
                  No pending approvals.
                </div>
              </template>
            </q-table>
          </div>
        </q-tab-panel>

        <q-tab-panel name="offers" class="q-pa-none">
          <div class="hris-toolbar">
            <div>
              <div class="text-subtitle1 text-weight-bold">Offers</div>
              <div class="text-caption text-grey-7">Monitor offer states and move candidates to onboarding.</div>
            </div>
          </div>
          <div class="hris-panel q-pa-none">
            <q-table :data="offers" :columns="offerColumns" row-key="id" separator="horizontal" flat>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="applicant" :props="props">{{ props.row.applicant }}</q-td>
                  <q-td key="position_title" :props="props">{{ props.row.position_title }}</q-td>
                  <q-td key="start_date" :props="props">{{ formatDate(props.row.start_date) }}</q-td>
                  <q-td key="salary" :props="props">{{ formatMoney(props.row.salary) }}</q-td>
                  <q-td key="status" :props="props">
                    <q-chip dense :color="offerStatusColor(props.row.status)" text-color="white">
                      {{ normalizeLabel(props.row.status) }}
                    </q-chip>
                  </q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-btn v-if="props.row.status === 'draft'" flat round dense class="hris-q-ghost"
                      icon="fas fa-paper-plane" @click="updateOfferStatus(props.row, 'send')"></q-btn>
                    <q-btn v-if="props.row.status === 'sent'" flat round dense class="hris-q-ghost" icon="fas fa-check"
                      @click="updateOfferStatus(props.row, 'accept')"></q-btn>
                    <q-btn v-if="props.row.status === 'sent'" flat round dense class="hris-q-ghost" icon="fas fa-times"
                      @click="updateOfferStatus(props.row, 'decline')"></q-btn>
                    <q-btn v-if="props.row.status !== 'accepted' && props.row.status !== 'withdrawn'" flat round dense
                      class="hris-q-ghost" icon="fas fa-ban" @click="updateOfferStatus(props.row, 'withdraw')"></q-btn>
                  </q-td>
                </q-tr>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">
                  No offers created yet.
                </div>
              </template>
            </q-table>
          </div>
        </q-tab-panel>
      </q-tab-panels>

      <q-dialog v-model="vacancyDialog" persistent>
        <div class="hris-modal hris-modal-lg">
          <div class="hris-modal-header">
            <h2 class="hris-modal-title">{{ vacancyForm.id ? 'Edit Vacancy' : 'New Vacancy' }}</h2>
            <q-btn flat round dense class="hris-q-ghost" icon="fas fa-times" v-close-popup></q-btn>
          </div>
          <div class="hris-modal-body">
            <div class="row q-col-gutter-sm">
              <div class="col-12 col-md-6"><q-input v-model="vacancyForm.title" dense outlined color="green"
                  label="Title"></q-input></div>
              <div class="col-12 col-md-6"><q-input v-model="vacancyForm.department" dense outlined color="green"
                  label="Department"></q-input>
              </div>
              <div class="col-12 col-md-6"><q-input v-model="vacancyForm.site" dense outlined color="green"
                  label="Site"></q-input></div>
              <div class="col-12 col-md-3"><q-input v-model="vacancyForm.employment_type" dense outlined color="green"
                  label="Employment Type"></q-input></div>
              <div class="col-12 col-md-3"><q-input v-model.number="vacancyForm.headcount" dense outlined color="green"
                  type="number" label="Headcount"></q-input></div>
              <div class="col-12 col-md-6"><q-input v-model.number="vacancyForm.salary_min" dense outlined color="green"
                  type="number" label="Salary Min"></q-input></div>
              <div class="col-12 col-md-6"><q-input v-model.number="vacancyForm.salary_max" dense outlined color="green"
                  type="number" label="Salary Max"></q-input></div>
              <div class="col-12"><q-input v-model="vacancyForm.job_description" dense outlined color="green"
                  type="textarea" label="Job Description"></q-input></div>
              <div class="col-12"><q-input v-model="vacancyForm.requirements" dense outlined color="green"
                  type="textarea" label="Requirements"></q-input></div>
            </div>
          </div>
          <div class="hris-modal-footer">
            <q-btn flat label="Cancel" class="hris-q-ghost" v-close-popup></q-btn>
            <q-btn unelevated label="Save" class="hris-q-primary" :loading="savingVacancy" @click="saveVacancy"></q-btn>
          </div>
        </div>
      </q-dialog>

      <q-dialog v-model="screeningDialog" persistent>
        <div class="hris-modal">
          <div class="hris-modal-header">
            <h2 class="hris-modal-title">Screen Applicant</h2>
            <q-btn flat round dense class="hris-q-ghost" icon="fas fa-times" v-close-popup></q-btn>
          </div>
          <div class="hris-modal-body">
            <q-select v-model="screeningForm.result" :options="['pass', 'fail', 'hold']" dense outlined color="green"
              label="Result" class="q-mb-sm"></q-select>
            <q-input v-model="screeningForm.expected_salary" dense outlined color="green" type="number"
              label="Expected Salary" class="q-mb-sm"></q-input>
            <q-input v-model="screeningForm.availability_date" dense outlined color="green" type="date"
              label="Availability Date" class="q-mb-sm"></q-input>
            <q-input v-model="screeningForm.qualification_notes" dense outlined color="green" type="textarea"
              label="Qualification Notes"></q-input>
          </div>
          <div class="hris-modal-footer">
            <q-btn flat label="Cancel" class="hris-q-ghost" v-close-popup></q-btn>
            <q-btn unelevated label="Save" class="hris-q-primary" :loading="savingAction"
              @click="submitScreening"></q-btn>
          </div>
        </div>
      </q-dialog>

      <q-dialog v-model="interviewDialog" persistent>
        <div class="hris-modal">
          <div class="hris-modal-header">
            <h2 class="hris-modal-title">Schedule Interview</h2>
            <q-btn flat round dense class="hris-q-ghost" icon="fas fa-times" v-close-popup></q-btn>
          </div>
          <div class="hris-modal-body">
            <q-input v-model="interviewForm.interview_type" dense outlined color="green" label="Interview Type"
              class="q-mb-sm"></q-input>
            <q-select v-model="interviewForm.interviewer_id" use-input hide-selected fill-input input-debounce="300"
              label="Interviewer" :options="employeeOptions" option-value="id"
              :option-label="emp => emp ? `${emp.name} (${emp.employee_id})` : ''" emit-value map-options outlined dense
              color="green" @filter="filterEmployees" class="q-mb-sm"></q-select>
            <q-input v-model="interviewForm.scheduled_at" dense outlined color="green" type="datetime-local"
              label="Schedule" class="q-mb-sm"></q-input>
            <q-input v-model="interviewForm.location_details" dense outlined color="green" label="Location"
              class="q-mb-sm"></q-input>

            <q-select v-model="interviewForm.platform" :options="platformOptions" label="Meeting Platform" dense
              outlined color="green" class="q-mb-sm" emit-value map-options>
              <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps" clickable v-close-popup @click="scope.toggleOption(scope.opt)">
                  <q-item-section avatar>
                    <q-avatar size="20px">
                      <img :src="scope.opt.icon" />
                    </q-avatar>
                  </q-item-section>
                  <q-item-section>{{ scope.opt.label }}</q-item-section>
                </q-item>
              </template>

              <template v-slot:selected-item="scope">
                <q-avatar size="18px" class="q-mr-sm">
                  <img :src="scope.opt.icon" />
                </q-avatar>
                {{ scope.opt.label }}
              </template>
            </q-select>

            <q-input v-model="interviewForm.meeting_link" dense outlined color="green" :label="meetingLinkLabel"
              class="q-mb-sm" />
          </div>
          <div class="hris-modal-footer">
            <q-btn flat label="Cancel" class="hris-q-ghost" v-close-popup></q-btn>
            <q-btn unelevated label="Schedule" class="hris-q-primary" :loading="savingAction"
              @click="scheduleInterview"></q-btn>
          </div>
        </div>
      </q-dialog>

      <q-dialog v-model="approvalDialog" persistent>
        <div class="hris-modal">
          <div class="hris-modal-header">
            <h2 class="hris-modal-title">Request Hiring Approval</h2>
            <q-btn flat round dense class="hris-q-ghost" icon="fas fa-times" v-close-popup></q-btn>
          </div>
          <div class="hris-modal-body">
            <q-select v-model="approvalForm.approver_ids" multiple use-chips use-input hide-selected fill-input
              input-debounce="300" label="Approver(s)" :options="employeeOptions" option-value="id"
              :option-label="emp => emp ? `${emp.name} (${emp.employee_id})` : ''" emit-value map-options outlined dense
              color="green" @filter="filterEmployees" class="q-mb-sm"></q-select>
            <q-input v-model="approvalForm.remarks" dense outlined color="green" type="textarea"
              label="Remarks"></q-input>
          </div>
          <div class="hris-modal-footer">
            <q-btn flat label="Cancel" class="hris-q-ghost" v-close-popup></q-btn>
            <q-btn unelevated label="Request" class="hris-q-primary" :loading="savingAction"
              @click="requestApproval"></q-btn>
          </div>
        </div>
      </q-dialog>

      <q-dialog v-model="offerDialog" persistent>
        <div class="hris-modal hris-modal-lg">
          <div class="hris-modal-header">
            <h2 class="hris-modal-title">Create Job Offer</h2>
            <q-btn flat round dense class="hris-q-ghost" icon="fas fa-times" v-close-popup></q-btn>
          </div>
          <div class="hris-modal-body">
            <div class="row q-col-gutter-sm">
              <div class="col-12 col-md-6"><q-input v-model="offerForm.position_title" dense outlined color="green"
                  label="Position"></q-input>
              </div>
              <div class="col-12 col-md-6"><q-input v-model="offerForm.employment_type" dense outlined color="green"
                  label="Employment Type"></q-input></div>
              <div class="col-12 col-md-6"><q-input v-model="offerForm.start_date" dense outlined color="green"
                  type="date" label="Start Date"></q-input></div>
              <div class="col-12 col-md-6"><q-input v-model.number="offerForm.salary" dense outlined color="green"
                  type="number" label="Salary"></q-input></div>
              <div class="col-12"><q-input v-model="offerForm.benefits_notes" dense outlined color="green"
                  type="textarea" label="Benefits Notes"></q-input></div>
            </div>
          </div>
          <div class="hris-modal-footer">
            <q-btn flat label="Cancel" class="hris-q-ghost" v-close-popup></q-btn>
            <q-btn unelevated label="Create" class="hris-q-primary" :loading="savingAction"
              @click="createOffer"></q-btn>
          </div>
        </div>
      </q-dialog>

      <q-dialog v-model="profileDialog">
        <div class="hris-modal" style="max-width: 960px;">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">{{ profile.application ? applicantName(profile.application) : `Applicant
                Profile`
                }}</h2>
              <p class="hris-modal-subtitle">{{ profile.application ? profile.application.tracking_id : '' }}</p>
            </div>
            <q-btn flat round dense class="hris-q-ghost" icon="fas fa-times" v-close-popup></q-btn>
          </div>
          <div class="hris-modal-body q-pa-none">
            <div class="hris-panel" style="padding: 0; margin: 0; border: none; box-shadow: none;">
              <q-tabs v-model="profileTab" dense active-color="green" indicator-color="green" align="left">
                <q-tab name="summary" label="Summary" icon="fas fa-id-card"></q-tab>
                <q-tab name="documents" label="Documents" icon="fas fa-file-invoice"></q-tab>
                <q-tab name="screening" label="Screening" icon="fas fa-filter"></q-tab>
                <q-tab name="interviews" label="Interviews" icon="fas fa-calendar-alt"></q-tab>
                <q-tab name="evaluations" label="Evaluations" icon="fas fa-clipboard-check"></q-tab>
                <q-tab name="approvals" label="Approvals" icon="fas fa-check-double"></q-tab>
                <q-tab name="offers" label="Offers" icon="fas fa-file-signature"></q-tab>
                <q-tab name="activity" label="Activity" icon="fas fa-history"></q-tab>
              </q-tabs>
            </div>

            <q-tab-panels v-model="profileTab" animated>
              <q-tab-panel name="summary">
                <div v-if="profile.application" class="row q-col-gutter-md">
                  <div class="col-12 col-md-6"><strong>Email:</strong> {{ profile.application.email || 'N/A' }}</div>
                  <div class="col-12 col-md-6"><strong>Mobile:</strong> {{ profile.application.mobile_number || 'N/A' }}
                  </div>
                  <div class="col-12 col-md-6"><strong>Position:</strong> {{ applicantPosition(profile.application) }}
                  </div>
                  <div class="col-12 col-md-6">
                    <strong>Status:</strong>
                    <q-chip dense :color="applicationStatusColor(profile.application.application_status)"
                      text-color="white" class="q-ml-sm">
                      {{ normalizeLabel(profile.application.application_status || 'applied') }}
                    </q-chip>
                  </div>
                  <div class="col-12 col-md-6"><strong>Date of Application:</strong> {{
                    formatDate(profile.application.date_of_application) }}</div>
                  <div class="col-12 col-md-6" v-if="profile.application.gender"><strong>Gender:</strong> {{
                    profile.application.gender }}</div>
                </div>
              </q-tab-panel>

              <q-tab-panel name="documents">
                <div class="row justify-between items-center q-mb-md">
                  <div class="text-subtitle1 text-weight-bold text-grey-8">Applicant Documents</div>
                  <div>
                    <input type="file" ref="resumeUpload" style="display:none" @change="uploadResume" />
                    <q-btn unelevated class="hris-q-primary" label="Upload Document" icon="fas fa-upload"
                      @click="$refs.resumeUpload.click()" :loading="loadingProfileDocs"></q-btn>
                  </div>
                </div>
                <q-list bordered separator class="rounded-borders">
                  <q-item v-for="doc in profile.documents" :key="doc.id">
                    <q-item-section avatar>
                      <q-icon name="fas fa-file-alt" color="primary" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="text-weight-bold">{{ doc.title }}</q-item-label>
                      <q-item-label caption>
                        Type: <q-chip dense outline size="xs" color="purple">{{ normalizeLabel(doc.document_type)
                          }}</q-chip>
                        &bull; Size: {{ formatSize(doc.file_size) }}
                        &bull; Uploaded: {{ formatDate(doc.created_at) }}
                      </q-item-label>
                    </q-item-section>
                    <q-item-section side>
                      <div class="row q-gutter-xs">
                        <q-btn flat round dense class="hris-q-ghost" icon="fas fa-download"
                          @click="downloadDocument(doc)">
                          <q-tooltip>Download File</q-tooltip>
                        </q-btn>
                        <q-btn flat round dense class="hris-q-ghost" icon="fas fa-trash-alt"
                          @click="deleteDocument(doc)">
                          <q-tooltip>Delete File</q-tooltip>
                        </q-btn>
                      </div>
                    </q-item-section>
                  </q-item>
                  <q-item v-if="!profile.documents || profile.documents.length === 0">
                    <q-item-section class="text-center text-grey-6 py-md">
                      <q-icon name="fas fa-folder-open" size="2em" class="q-mb-sm block" style="margin: 0 auto;" />
                      No documents uploaded.
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-tab-panel>

              <q-tab-panel name="screening">
                <div class="row justify-between items-center q-mb-md">
                  <div class="text-subtitle1 text-weight-bold text-grey-8">Screening Results</div>
                  <q-btn unelevated class="hris-q-primary" icon="fas fa-plus" label="Add Screening"
                    @click="openScreeningDialog(profile.application)"></q-btn>
                </div>
                <q-list bordered separator class="rounded-borders">
                  <q-item v-for="scr in profile.screening" :key="scr.id">
                    <q-item-section>
                      <div class="row items-center justify-between">
                        <div class="row items-center q-gutter-sm">
                          <span class="text-weight-bold">Result:</span>
                          <q-chip dense
                            :color="scr.result === 'pass' ? 'green' : (scr.result === 'hold' ? 'orange' : 'red')"
                            text-color="white">
                            {{ normalizeLabel(scr.result) }}
                          </q-chip>
                        </div>
                        <div class="text-caption text-grey-7">
                          Screened by: {{ employeeName(scr.screener) }} &bull; {{ formatDate(scr.created_at) }}
                        </div>
                      </div>
                      <div class="q-mt-sm row q-col-gutter-md">
                        <div class="col-12 col-md-6" v-if="scr.expected_salary">
                          <strong>Expected Salary:</strong> {{ formatMoney(scr.expected_salary) }}
                        </div>
                        <div class="col-12 col-md-6" v-if="scr.availability_date">
                          <strong>Availability Date:</strong> {{ formatDate(scr.availability_date) }}
                        </div>
                        <div class="col-12 col-md-6" v-if="scr.source">
                          <strong>Source:</strong> {{ scr.source }}
                        </div>
                        <div class="col-12 col-md-6" v-if="scr.recommended_next_step">
                          <strong>Recommended Next Step:</strong> {{ scr.recommended_next_step }}
                        </div>
                        <div class="col-12" v-if="scr.qualification_notes">
                          <strong>Qualification Notes:</strong>
                          <div class="bg-grey-2 q-pa-sm rounded-borders text-caption q-mt-xs">{{ scr.qualification_notes
                            }}
                          </div>
                        </div>
                        <div class="col-12" v-if="scr.remarks">
                          <strong>Remarks:</strong>
                          <div class="bg-grey-2 q-pa-sm rounded-borders text-caption q-mt-xs">{{ scr.remarks }}</div>
                        </div>
                      </div>
                    </q-item-section>
                  </q-item>
                  <q-item v-if="!profile.screening || profile.screening.length === 0">
                    <q-item-section class="text-center text-grey-6 py-md">
                      <q-icon name="fas fa-filter" size="2em" class="q-mb-sm block" style="margin: 0 auto;" />
                      No screenings completed.
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-tab-panel>

              <q-tab-panel name="interviews">
                <div class="row justify-between items-center q-mb-md">
                  <div class="text-subtitle1 text-weight-bold text-grey-8">Scheduled & Past Interviews</div>
                  <q-btn unelevated class="hris-q-primary" icon="fas fa-plus" label="Schedule Interview"
                    @click="openInterviewDialog(profile.application)"></q-btn>
                </div>
                <q-list bordered separator class="rounded-borders">
                  <q-item v-for="int in profile.interviews" :key="int.id">
                    <q-item-section>
                      <div class="row items-center justify-between">
                        <div class="text-weight-bold text-primary">{{ int.interview_type }}</div>
                        <div class="row items-center q-gutter-xs">
                          <q-chip dense
                            :color="int.status === 'completed' ? 'green' : (int.status === 'scheduled' ? 'blue' : 'red')"
                            text-color="white">
                            {{ normalizeLabel(int.status) }}
                          </q-chip>
                          <q-chip v-if="int.meeting_link && int.meeting_link.includes('zoom')" dense color="cyan"
                            text-color="white" icon="fas fa-video">
                            Zoom
                          </q-chip>
                        </div>
                      </div>

                      <div class="q-mt-sm row q-col-gutter-sm text-caption">
                        <div class="col-12 col-md-6">
                          <strong>Interviewer:</strong> {{ employeeName(int.interviewer) }}
                        </div>
                        <div class="col-12 col-md-6">
                          <strong>Schedule:</strong> {{ formatDate(int.scheduled_at) }}
                        </div>
                        <div class="col-12 col-md-6" v-if="int.location_details">
                          <strong>Location:</strong> {{ int.location_details }}
                        </div>
                        <div class="col-12 col-md-6" v-if="int.meeting_link">
                          <strong>Meeting Link:</strong>
                          <a :href="int.meeting_link" target="_blank" class="text-primary text-weight-bold q-ml-xs">
                            Join Meeting <q-icon name="fas fa-external-link-alt" size="xs" />
                          </a>
                        </div>
                        <div class="col-12" v-if="int.notes">
                          <strong>Notes:</strong>
                          <div class="bg-grey-2 q-pa-sm rounded-borders q-mt-xs">{{ int.notes }}</div>
                        </div>
                      </div>

                      <div class="q-mt-md row justify-end q-gutter-sm" v-if="int.status === 'scheduled'">
                        <q-btn size="sm" unelevated class="hris-q-outline" icon="fas fa-check" label="Complete"
                          @click="updateInterviewStatus(int, 'completed')"></q-btn>
                        <q-btn size="sm" unelevated class="hris-q-ghost" icon="fas fa-times" label="Cancel"
                          @click="updateInterviewStatus(int, 'cancelled')"></q-btn>
                        <q-btn size="sm" unelevated class="hris-q-primary" icon="fas fa-clipboard-check"
                          label="Evaluate" @click="openEvaluationDialog(int, profile.application)"></q-btn>
                      </div>
                      <div class="q-mt-md row justify-end q-gutter-sm"
                        v-else-if="int.status === 'completed' && !hasEvaluation(int.id)">
                        <q-btn size="sm" unelevated class="hris-q-primary" icon="fas fa-clipboard-check"
                          label="Evaluate Candidate" @click="openEvaluationDialog(int, profile.application)"></q-btn>
                      </div>
                    </q-item-section>
                  </q-item>
                  <q-item v-if="!profile.interviews || profile.interviews.length === 0">
                    <q-item-section class="text-center text-grey-6 py-md">
                      <q-icon name="fas fa-calendar-times" size="2em" class="q-mb-sm block" style="margin: 0 auto;" />
                      No interviews scheduled.
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-tab-panel>

              <q-tab-panel name="evaluations">
                <div class="text-subtitle1 text-weight-bold text-grey-8 q-mb-md">Candidate Evaluations</div>
                <q-list bordered separator class="rounded-borders">
                  <q-item v-for="ev in profile.evaluations" :key="ev.id">
                    <q-item-section>
                      <div class="row items-center justify-between">
                        <div class="row items-center q-gutter-sm">
                          <span class="text-weight-bold">Recommendation:</span>
                          <q-chip dense
                            :color="ev.recommendation === 'hire' ? 'green' : (ev.recommendation === 'hold' ? 'orange' : 'red')"
                            text-color="white">
                            {{ normalizeLabel(ev.recommendation) }}
                          </q-chip>
                          <span class="text-weight-bold text-subtitle2 text-indigo">Overall Score: {{ ev.overall_score
                            }}/5.0</span>
                        </div>
                        <div class="text-caption text-grey-7">
                          Evaluated by: {{ employeeName(ev.evaluator) }} &bull; {{ formatDate(ev.created_at) }}
                        </div>
                      </div>

                      <div class="q-mt-md grid-scores row q-col-gutter-sm text-caption">
                        <div class="col-6 col-md-4">
                          <strong>Communication:</strong>
                          <div class="row items-center q-gutter-xs">
                            <q-rating readonly v-model="ev.communication_score" :max="5" size="1.2em" color="amber" />
                            <span>({{ ev.communication_score }})</span>
                          </div>
                        </div>
                        <div class="col-6 col-md-4">
                          <strong>Experience Fit:</strong>
                          <div class="row items-center q-gutter-xs">
                            <q-rating readonly v-model="ev.experience_score" :max="5" size="1.2em" color="amber" />
                            <span>({{ ev.experience_score }})</span>
                          </div>
                        </div>
                        <div class="col-6 col-md-4">
                          <strong>Technical Skills:</strong>
                          <div class="row items-center q-gutter-xs">
                            <q-rating readonly v-model="ev.technical_score" :max="5" size="1.2em" color="amber" />
                            <span>({{ ev.technical_score }})</span>
                          </div>
                        </div>
                        <div class="col-6 col-md-4">
                          <strong>Culture Fit:</strong>
                          <div class="row items-center q-gutter-xs">
                            <q-rating readonly v-model="ev.culture_fit_score" :max="5" size="1.2em" color="amber" />
                            <span>({{ ev.culture_fit_score }})</span>
                          </div>
                        </div>
                        <div class="col-6 col-md-4">
                          <strong>Availability:</strong>
                          <div class="row items-center q-gutter-xs">
                            <q-rating readonly v-model="ev.availability_score" :max="5" size="1.2em" color="amber" />
                            <span>({{ ev.availability_score }})</span>
                          </div>
                        </div>
                        <div class="col-6 col-md-4">
                          <strong>Compensation Fit:</strong>
                          <div class="row items-center q-gutter-xs">
                            <q-rating readonly v-model="ev.compensation_fit_score" :max="5" size="1.2em"
                              color="amber" />
                            <span>({{ ev.compensation_fit_score }})</span>
                          </div>
                        </div>
                      </div>

                      <div class="q-mt-md" v-if="ev.comments">
                        <strong>Comments:</strong>
                        <div class="bg-grey-2 q-pa-sm rounded-borders text-caption q-mt-xs">{{ ev.comments }}</div>
                      </div>
                    </q-item-section>
                  </q-item>
                  <q-item v-if="!profile.evaluations || profile.evaluations.length === 0">
                    <q-item-section class="text-center text-grey-6 py-md">
                      <q-icon name="fas fa-clipboard-check" size="2em" class="q-mb-sm block" style="margin: 0 auto;" />
                      No evaluations submitted.
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-tab-panel>

              <q-tab-panel name="approvals">
                <div class="row justify-between items-center q-mb-md">
                  <div class="text-subtitle1 text-weight-bold text-grey-8">Hiring Approvals Workflow</div>
                  <q-btn unelevated class="hris-q-primary" icon="fas fa-plus" label="Request Approval"
                    @click="openApprovalDialog(profile.application)"></q-btn>
                </div>
                <q-list bordered separator class="rounded-borders">
                  <q-item v-for="appr in profile.approvals" :key="appr.id">
                    <q-item-section>
                      <div class="row items-center justify-between">
                        <div class="row items-center q-gutter-sm">
                          <span class="text-weight-bold">Level {{ appr.approval_level }}:</span>
                          <q-chip dense
                            :color="appr.status === 'approved' ? 'green' : (appr.status === 'pending' ? 'orange' : 'red')"
                            text-color="white">
                            {{ normalizeLabel(appr.status) }}
                          </q-chip>
                        </div>
                        <div class="text-caption text-grey-7">
                          Requested: {{ formatDate(appr.created_at) }}
                        </div>
                      </div>
                      <div class="q-mt-sm row q-col-gutter-sm text-caption">
                        <div class="col-12 col-md-6">
                          <strong>Approver:</strong> {{ employeeName(appr.approver) }}
                        </div>
                        <div class="col-12 col-md-6">
                          <strong>Requested By:</strong> {{ employeeName(appr.requester) }}
                        </div>
                        <div class="col-12 col-md-6" v-if="appr.acted_at">
                          <strong>Acted At:</strong> {{ formatDate(appr.acted_at) }}
                        </div>
                        <div class="col-12" v-if="appr.remarks">
                          <strong>Remarks:</strong>
                          <div class="bg-grey-2 q-pa-sm rounded-borders q-mt-xs">{{ appr.remarks }}</div>
                        </div>
                      </div>
                    </q-item-section>
                  </q-item>
                  <q-item v-if="!profile.approvals || profile.approvals.length === 0">
                    <q-item-section class="text-center text-grey-6 py-md">
                      <q-icon name="fas fa-check-double" size="2em" class="q-mb-sm block" style="margin: 0 auto;" />
                      No approvals requested.
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-tab-panel>

              <q-tab-panel name="offers">
                <div class="row justify-between items-center q-mb-md">
                  <div class="text-subtitle1 text-weight-bold text-grey-8">Job Offers</div>
                  <q-btn
                    :disabled="profile.application && profile.application.application_status !== 'approved_for_offer'"
                    unelevated class="hris-q-primary" icon="fas fa-plus" label="Create Job Offer"
                    @click="openOfferDialog(profile.application)"></q-btn>
                </div>
                <q-list bordered separator class="rounded-borders">
                  <q-item v-for="off in profile.offers" :key="off.id">
                    <q-item-section>
                      <div class="row items-center justify-between">
                        <div class="text-weight-bold text-primary">{{ off.position_title }}</div>
                        <div class="row items-center q-gutter-xs">
                          <q-chip dense :color="offerStatusColor(off.status)" text-color="white">
                            {{ normalizeLabel(off.status) }}
                          </q-chip>
                        </div>
                      </div>
                      <div class="q-mt-sm row q-col-gutter-sm text-caption">
                        <div class="col-12 col-md-6">
                          <strong>Type:</strong> {{ off.employment_type }}
                        </div>
                        <div class="col-12 col-md-6">
                          <strong>Start Date:</strong> {{ formatDate(off.start_date) }}
                        </div>
                        <div class="col-12 col-md-6">
                          <strong>Salary:</strong> {{ formatMoney(off.salary) }}
                        </div>
                        <div class="col-12 col-md-6" v-if="off.offer_expiry">
                          <strong>Offer Expiry:</strong> {{ formatDate(off.offer_expiry) }}
                        </div>
                        <div class="col-12" v-if="off.benefits_notes">
                          <strong>Benefits Notes:</strong>
                          <div class="bg-grey-2 q-pa-sm rounded-borders q-mt-xs">{{ off.benefits_notes }}</div>
                        </div>
                        <div class="col-12" v-if="off.response_notes">
                          <strong>Response Notes:</strong>
                          <div class="bg-grey-2 q-pa-sm rounded-borders q-mt-xs">{{ off.response_notes }}</div>
                        </div>
                      </div>

                      <div class="q-mt-md row justify-end q-gutter-sm">
                        <q-btn v-if="off.status === 'draft'" size="sm" unelevated class="hris-q-primary"
                          icon="fas fa-paper-plane" label="Send Offer" @click="updateOfferStatus(off, 'send')"></q-btn>
                        <q-btn v-if="off.status === 'sent'" size="sm" unelevated class="hris-q-outline"
                          icon="fas fa-check" label="Accept" @click="updateOfferStatus(off, 'accept')"></q-btn>
                        <q-btn v-if="off.status === 'sent'" size="sm" unelevated class="hris-q-ghost"
                          icon="fas fa-times" label="Decline" @click="updateOfferStatus(off, 'decline')"></q-btn>
                        <q-btn v-if="off.status !== 'accepted' && off.status !== 'withdrawn'" size="sm" unelevated
                          class="hris-q-ghost" icon="fas fa-ban" label="Withdraw"
                          @click="updateOfferStatus(off, 'withdraw')"></q-btn>
                      </div>
                    </q-item-section>
                  </q-item>
                  <q-item v-if="!profile.offers || profile.offers.length === 0">
                    <q-item-section class="text-center text-grey-6 py-md">
                      <q-icon name="fas fa-file-signature" size="2em" class="q-mb-sm block" style="margin: 0 auto;" />
                      No offers created yet.
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-tab-panel>

              <q-tab-panel name="activity">
                <q-list bordered separator class="rounded-borders">
                  <q-item v-for="item in profile.activity" :key="item.id">
                    <q-item-section>
                      <q-item-label class="text-weight-bold">{{ normalizeLabel(item.action) }}</q-item-label>
                      <q-item-label caption>
                        {{ item.from_status || 'Start' }} &rarr; {{ item.to_status || 'End' }}
                        &bull; Actor: {{ employeeName(item.actor) }}
                        &bull; Date: {{ formatDate(item.created_at) }}
                      </q-item-label>
                      <q-item-label caption v-if="item.remarks" class="q-mt-xs">
                        <strong>Remarks:</strong> {{ item.remarks }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item v-if="!profile.activity || profile.activity.length === 0">
                    <q-item-section class="text-center text-grey-6 py-md">
                      <q-icon name="fas fa-history" size="2em" class="q-mb-sm block" style="margin: 0 auto;" />
                      No activity yet.
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-tab-panel>
            </q-tab-panels>
          </div>
        </div>
      </q-dialog>

      <q-dialog v-model="evaluationDialog" persistent>
        <div class="hris-modal hris-modal-lg">
          <div class="hris-modal-header">
            <h2 class="hris-modal-title">Candidate Evaluation</h2>
            <q-btn flat round dense class="hris-q-ghost" icon="fas fa-times" v-close-popup></q-btn>
          </div>
          <div class="hris-modal-body">
            <div class="text-subtitle2 q-mb-md">
              Evaluate: <span class="text-weight-bold">{{ selectedApplication ?
                applicantName(selectedApplication) : '' }}</span>
            </div>

            <div class="q-mb-md">
              <div class="row items-center justify-between">
                <span>Communication Skills</span>
                <q-rating v-model="evaluationForm.communication_score" :max="5" size="1.8em" color="amber"
                  icon="fas fa-star"></q-rating>
              </div>
            </div>
            <div class="q-mb-md">
              <div class="row items-center justify-between">
                <span>Experience Fit</span>
                <q-rating v-model="evaluationForm.experience_score" :max="5" size="1.8em" color="amber"
                  icon="fas fa-star"></q-rating>
              </div>
            </div>
            <div class="q-mb-md">
              <div class="row items-center justify-between">
                <span>Technical Skills</span>
                <q-rating v-model="evaluationForm.technical_score" :max="5" size="1.8em" color="amber"
                  icon="fas fa-star"></q-rating>
              </div>
            </div>
            <div class="q-mb-md">
              <div class="row items-center justify-between">
                <span>Culture Fit</span>
                <q-rating v-model="evaluationForm.culture_fit_score" :max="5" size="1.8em" color="amber"
                  icon="fas fa-star"></q-rating>
              </div>
            </div>
            <div class="q-mb-md">
              <div class="row items-center justify-between">
                <span>Availability</span>
                <q-rating v-model="evaluationForm.availability_score" :max="5" size="1.8em" color="amber"
                  icon="fas fa-star"></q-rating>
              </div>
            </div>
            <div class="q-mb-md">
              <div class="row items-center justify-between">
                <span>Compensation Fit</span>
                <q-rating v-model="evaluationForm.compensation_fit_score" :max="5" size="1.8em" color="amber"
                  icon="fas fa-star"></q-rating>
              </div>
            </div>

            <q-select v-model="evaluationForm.recommendation" :options="[
              { label: 'Hire', value: 'hire' },
              { label: 'Hold', value: 'hold' },
              { label: 'Reject', value: 'reject' }
            ]" emit-value map-options dense outlined color="green" label="Recommendation" class="q-mb-sm"></q-select>
            <q-input v-model="evaluationForm.comments" type="textarea" dense outlined color="green"
              label="Comments / Remarks"></q-input>
          </div>
          <div class="hris-modal-footer">
            <q-btn flat label="Cancel" class="hris-q-ghost" v-close-popup></q-btn>
            <q-btn unelevated label="Submit Evaluation" class="hris-q-primary" :loading="savingAction"
              @click="submitEvaluation"></q-btn>
          </div>
        </div>
      </q-dialog>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ApplicantTrackingDashboard",
  data() {
    return {
      platformOptions: [
        { label: 'Zoom Meeting', value: 'zoom', icon: require('@/assets/zoom-2025.svg') },
        { label: 'Microsoft Teams', value: 'teams', icon: require('@/assets/microsoft-teams.svg') }
      ],
      tab: "vacancies",
      vacancies: [],
      applications: [],
      approvals: [],
      offers: [],
      profile: {},
      selectedApplication: null,
      selectedInterview: null,
      vacancyFilter: "",
      applicationSearch: "",
      applicationStatusFilter: null,
      loadingVacancies: false,
      loadingApplications: false,
      loadingApprovals: false,
      loadingProfileDocs: false,
      savingVacancy: false,
      savingAction: false,
      vacancyDialog: false,
      screeningDialog: false,
      interviewDialog: false,
      approvalDialog: false,
      offerDialog: false,
      evaluationDialog: false,
      profileDialog: false,
      profileTab: "summary",
      approvalApprovers: "",
      employeeOptions: [],
      applicationStatuses: [
        "applied",
        "screening",
        "shortlisted",
        "initial_interview",
        "final_interview",
        "evaluation",
        "approval_pending",
        "approved_for_offer",
        "offer_sent",
        "offer_accepted",
        "hired",
        "onboarding",
        "rejected"
      ],
      vacancyForm: this.emptyVacancyForm(),
      screeningForm: { result: "pass", qualification_notes: "", expected_salary: null, availability_date: null },
      interviewForm: { interview_type: "Initial Interview", interviewer_id: null, scheduled_at: "", location_details: "", meeting_link: "", platform: "zoom" },
      approvalForm: { approver_ids: [], remarks: "" },
      evaluationForm: { communication_score: 3, experience_score: 3, technical_score: 3, culture_fit_score: 3, availability_score: 3, compensation_fit_score: 3, recommendation: "hold", comments: "" },
      offerForm: { position_title: "", employment_type: "Regular", start_date: "", salary: null, benefits_notes: "" },
      vacancyColumns: [
        { name: "title", label: "Vacancy", field: "title", align: "left", sortable: true },
        { name: "employment_type", label: "Type", field: "employment_type", align: "left" },
        { name: "headcount", label: "Headcount", field: "headcount", align: "center" },
        { name: "recruiter", label: "Recruiter", field: "recruiter", align: "left" },
        { name: "status", label: "Status", field: "status", align: "center" },
        { name: "open_date", label: "Open Date", field: "open_date", align: "left" },
        { name: "actions", label: "Actions", field: "actions", align: "center" }
      ],
      applicationColumns: [
        { name: "tracking_id", label: "Tracking ID", field: "tracking_id", align: "left" },
        { name: "name", label: "Applicant", field: "lastname", align: "left" },
        { name: "position", label: "Position", field: "position_applying_for", align: "left" },
        { name: "application_status", label: "Status", field: "application_status", align: "center" },
        { name: "date_of_application", label: "Applied", field: "date_of_application", align: "left" },
        { name: "actions", label: "Actions", field: "actions", align: "center" }
      ],
      approvalColumns: [
        { name: "applicant", label: "Applicant", field: "applicant", align: "left" },
        { name: "vacancy", label: "Vacancy", field: "vacancy", align: "left" },
        { name: "approval_level", label: "Level", field: "approval_level", align: "center" },
        { name: "requested_by", label: "Requested By", field: "requested_by", align: "left" },
        { name: "created_at", label: "Requested", field: "created_at", align: "left" },
        { name: "actions", label: "Actions", field: "actions", align: "center" }
      ],
      offerColumns: [
        { name: "applicant", label: "Applicant", field: "applicant", align: "left" },
        { name: "position_title", label: "Position", field: "position_title", align: "left" },
        { name: "start_date", label: "Start Date", field: "start_date", align: "left" },
        { name: "salary", label: "Salary", field: "salary", align: "right" },
        { name: "status", label: "Status", field: "status", align: "center" },
        { name: "actions", label: "Actions", field: "actions", align: "center" }
      ]
    };
  },
  computed: {
    meetingLinkLabel() {
      const platform = this.platformOptions.find(
        (p) => p.value === this.interviewForm.platform
      )
      const name = platform ? platform.label.replace(' Meeting', '') : 'the platform'
      return `Meeting Link (Optional - Auto-generated via ${name} if empty)`
    },
    summaryCards() {
      return [
        { label: 'Vacancies', value: this.vacancies.length, icon: 'fas fa-briefcase' },
        { label: 'Applicants', value: this.applications.length, icon: 'fas fa-users' },
        { label: 'Approvals', value: this.approvals.length, icon: 'fas fa-check-double' },
        { label: 'Offers', value: this.offers.length, icon: 'fas fa-file-signature' }
      ];
    }
  },
  mounted() {
    this.refreshAll();
  },
  methods: {
    headers() {
      return { headers: { Authorization: `Bearer ${this.token}` } };
    },
    emptyVacancyForm() {
      return {
        id: null,
        title: "",
        department: "",
        site: "",
        employment_type: "Regular",
        headcount: 1,
        job_description: "",
        requirements: "",
        salary_min: null,
        salary_max: null
      };
    },
    refreshAll() {
      this.getVacancies();
      this.getApplications();
      this.getApprovals();
    },
    notify(type, message) {
      this.$q.notify({ type, message, position: "top-right" });
    },
    getVacancies() {
      this.loadingVacancies = true;
      axios
        .get(`${this.apiUrl}/recruitments/ats/vacancies`, this.headers())
        .then(res => {
          this.vacancies = res.data.data || [];
        })
        .catch(() => this.notify("negative", "Unable to load vacancies."))
        .finally(() => {
          this.loadingVacancies = false;
        });
    },
    getApplications() {
      this.loadingApplications = true;
      const params = {};
      if (this.applicationStatusFilter) params.status = this.applicationStatusFilter;
      if (this.applicationSearch) params.search = this.applicationSearch;
      axios
        .get(`${this.apiUrl}/recruitments/ats/applications`, Object.assign(this.headers(), { params }))
        .then(res => {
          this.applications = res.data.data || [];
        })
        .catch(() => this.notify("negative", "Unable to load applicants."))
        .finally(() => {
          this.loadingApplications = false;
        });
    },
    getApprovals() {
      this.loadingApprovals = true;
      axios
        .get(`${this.apiUrl}/recruitments/ats/approvals/pending`, this.headers())
        .then(res => {
          this.approvals = res.data.data || [];
        })
        .catch(() => this.notify("negative", "Unable to load approvals."))
        .finally(() => {
          this.loadingApprovals = false;
        });
    },
    openVacancyDialog(row) {
      this.vacancyForm = row ? Object.assign({}, row) : this.emptyVacancyForm();
      this.vacancyDialog = true;
    },
    saveVacancy() {
      this.savingVacancy = true;
      const request = this.vacancyForm.id
        ? axios.put(`${this.apiUrl}/recruitments/ats/vacancies/${this.vacancyForm.id}`, this.vacancyForm, this.headers())
        : axios.post(`${this.apiUrl}/recruitments/ats/vacancies`, this.vacancyForm, this.headers());
      request
        .then(() => {
          this.notify("positive", "Vacancy saved.");
          this.vacancyDialog = false;
          this.getVacancies();
        })
        .catch(err => this.notify("negative", this.errorMessage(err, "Unable to save vacancy.")))
        .finally(() => {
          this.savingVacancy = false;
        });
    },
    publishVacancy(row) {
      axios.post(`${this.apiUrl}/recruitments/ats/vacancies/${row.id}/publish`, {}, this.headers()).then(this.getVacancies);
    },
    closeVacancy(row) {
      axios.post(`${this.apiUrl}/recruitments/ats/vacancies/${row.id}/close`, {}, this.headers()).then(this.getVacancies);
    },
    openApplicationProfile(row) {
      axios
        .get(`${this.apiUrl}/recruitments/ats/applications/${row.id}`, this.headers())
        .then(res => {
          this.profile = res.data.data || {};
          this.profileTab = "summary";
          this.profileDialog = true;
          this.syncOffersFromProfile();
        })
        .catch(() => this.notify("negative", "Unable to load applicant profile."));
    },
    syncOffersFromProfile() {
      if (!this.profile.offers || !this.profile.application) return;
      const applicant = this.applicantName(this.profile.application);
      this.profile.offers.forEach(offer => {
        offer.applicant = applicant;
        if (!this.offers.find(item => item.id === offer.id)) this.offers.push(offer);
      });
    },
    openScreeningDialog(row) {
      this.selectedApplication = row;
      this.screeningForm = { result: "pass", qualification_notes: "", expected_salary: null, availability_date: null };
      this.screeningDialog = true;
    },
    submitScreening() {
      this.savingAction = true;
      axios
        .post(`${this.apiUrl}/recruitments/ats/applications/${this.selectedApplication.id}/screening`, this.screeningForm, this.headers())
        .then(() => {
          this.notify("positive", "Screening saved.");
          this.screeningDialog = false;
          if (this.profileDialog && this.profile.application) {
            this.openApplicationProfile(this.profile.application);
          }
          this.getApplications();
        })
        .catch(err => this.notify("negative", this.errorMessage(err, "Unable to save screening.")))
        .finally(() => {
          this.savingAction = false;
        });
    },
    openInterviewDialog(row) {
      this.selectedApplication = row;
      this.interviewForm = { interview_type: "Initial Interview", interviewer_id: null, scheduled_at: "", location_details: "", meeting_link: "" };
      this.interviewDialog = true;
      this.filterEmployees('', () => { });
    },
    scheduleInterview() {
      this.savingAction = true;
      axios
        .post(`${this.apiUrl}/recruitments/ats/applications/${this.selectedApplication.id}/interviews`, this.interviewForm, this.headers())
        .then(() => {
          this.notify("positive", "Interview scheduled.");
          this.interviewDialog = false;
          if (this.profileDialog && this.profile.application) {
            this.openApplicationProfile(this.profile.application);
          }
          this.getApplications();
        })
        .catch(err => this.notify("negative", this.errorMessage(err, "Unable to schedule interview.")))
        .finally(() => {
          this.savingAction = false;
        });
    },
    openApprovalDialog(row) {
      this.selectedApplication = row;
      this.approvalForm = { approver_ids: [], remarks: "" };
      this.approvalDialog = true;
      this.filterEmployees('', () => { });
    },
    requestApproval() {
      if (!this.approvalForm.approver_ids || this.approvalForm.approver_ids.length === 0) {
        this.notify("negative", "At least one approver is required.");
        return;
      }
      this.savingAction = true;
      axios
        .post(`${this.apiUrl}/recruitments/ats/applications/${this.selectedApplication.id}/request-approval`, {
          approver_ids: this.approvalForm.approver_ids,
          remarks: this.approvalForm.remarks
        }, this.headers())
        .then(() => {
          this.notify("positive", "Approval requested.");
          this.approvalDialog = false;
          if (this.profileDialog && this.profile.application) {
            this.openApplicationProfile(this.profile.application);
          }
          this.getApplications();
          this.getApprovals();
        })
        .catch(err => this.notify("negative", this.errorMessage(err, "Unable to request approval.")))
        .finally(() => {
          this.savingAction = false;
        });
    },
    actOnApproval(row, action) {
      axios
        .post(`${this.apiUrl}/recruitments/ats/approvals/${row.id}/${action}`, {}, this.headers())
        .then(() => {
          this.notify("positive", "Approval updated.");
          if (this.profileDialog && this.profile.application) {
            this.openApplicationProfile(this.profile.application);
          }
          this.getApprovals();
          this.getApplications();
        })
        .catch(err => this.notify("negative", this.errorMessage(err, "Unable to update approval.")));
    },
    openOfferDialog(row) {
      this.selectedApplication = row;
      this.offerForm = {
        position_title: this.applicantPosition(row),
        employment_type: "Regular",
        start_date: "",
        salary: null,
        benefits_notes: ""
      };
      this.offerDialog = true;
    },
    createOffer() {
      this.savingAction = true;
      axios
        .post(`${this.apiUrl}/recruitments/ats/applications/${this.selectedApplication.id}/offers`, this.offerForm, this.headers())
        .then(res => {
          const offer = res.data.data;
          offer.applicant = this.applicantName(this.selectedApplication);
          this.offers.unshift(offer);
          this.notify("positive", "Offer created.");
          this.offerDialog = false;
          if (this.profileDialog && this.profile.application) {
            this.openApplicationProfile(this.profile.application);
          }
          this.getApplications();
        })
        .catch(err => this.notify("negative", this.errorMessage(err, "Unable to create offer.")))
        .finally(() => {
          this.savingAction = false;
        });
    },
    updateOfferStatus(row, status) {
      axios
        .post(`${this.apiUrl}/recruitments/ats/offers/${row.id}/${status}`, {}, this.headers())
        .then(res => {
          Object.assign(row, res.data.data);
          this.notify("positive", "Offer updated.");
          if (this.profileDialog && this.profile.application) {
            this.openApplicationProfile(this.profile.application);
          }
          this.getApplications();
        })
        .catch(err => this.notify("negative", this.errorMessage(err, "Unable to update offer.")));
    },
    filterEmployees(val, update) {
      axios
        .get(`${this.apiUrl}/employees/search?keyword=${val}`, this.headers())
        .then(res => {
          update(() => {
            this.employeeOptions = res.data.data || [];
          });
        })
        .catch(() => {
          update(() => {
            this.employeeOptions = [];
          });
        });
    },

    uploadResume(event) {
      const file = event.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", "resume");
      formData.append("title", file.name);

      this.loadingProfileDocs = true;
      axios
        .post(`${this.apiUrl}/recruitments/ats/applications/${this.profile.application.id}/documents`, formData, {
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "multipart/form-data"
          }
        })
        .then(res => {
          this.notify("positive", "Document uploaded successfully.");
          this.openApplicationProfile(this.profile.application);
        })
        .catch(err => {
          this.notify("negative", this.errorMessage(err, "Upload failed."));
        })
        .finally(() => {
          this.loadingProfileDocs = false;
        });
    },
    deleteDocument(doc) {
      this.$q
        .dialog({
          title: "Delete Document",
          message: `Are you sure you want to delete "${doc.title}"?`,
          cancel: true,
          persistent: true
        })
        .onOk(() => {
          axios
            .delete(`${this.apiUrl}/recruitments/ats/documents/${doc.id}`, this.headers())
            .then(() => {
              this.notify("positive", "Document deleted successfully.");
              this.openApplicationProfile(this.profile.application);
            })
            .catch(err => this.notify("negative", this.errorMessage(err, "Delete failed.")));
        });
    },
    downloadDocument(doc) {
      axios
        .get(`${this.apiUrl}/recruitments/ats/documents/${doc.id}/download`, {
          headers: { Authorization: "Bearer " + this.token },
          responseType: "blob"
        })
        .then((res) => {
          const contentType = res.headers["content-type"] || doc.mime_type || "application/octet-stream";
          const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
          const link = document.createElement("a");
          link.href = blobUrl;
          const disposition = res.headers["content-disposition"] || "";
          const match = disposition.match(/filename="?([^"]+)"?/);
          link.download = match ? match[1] : doc.title;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch((err) => {
          this.$q.notify({ type: "negative", message: "Unable to download document." });
        })
        .finally(() => {
          this.downloadingId = null;
        });
    },
    updateInterviewStatus(interview, status) {
      this.savingAction = true;
      axios
        .post(`${this.apiUrl}/recruitments/ats/interviews/${interview.id}`, { status }, this.headers())
        .then(() => {
          this.notify("positive", `Interview marked as ${status}.`);
          if (this.profileDialog && this.profile.application) {
            this.openApplicationProfile(this.profile.application);
          }
          this.getApplications();
        })
        .catch(err => {
          this.notify("negative", this.errorMessage(err, "Unable to update interview status."));
        })
        .finally(() => {
          this.savingAction = false;
        });
    },
    openEvaluationDialog(interview, appRow) {
      this.selectedInterview = interview;
      this.selectedApplication = appRow;
      this.evaluationForm = {
        communication_score: 3,
        experience_score: 3,
        technical_score: 3,
        culture_fit_score: 3,
        availability_score: 3,
        compensation_fit_score: 3,
        recommendation: "hold",
        comments: ""
      };
      this.evaluationDialog = true;
    },
    submitEvaluation() {
      this.savingAction = true;
      axios
        .post(`${this.apiUrl}/recruitments/ats/interviews/${this.selectedInterview.id}/evaluation`, this.evaluationForm, this.headers())
        .then(() => {
          this.notify("positive", "Evaluation submitted successfully.");
          this.evaluationDialog = false;
          if (this.profileDialog && this.profile.application) {
            this.openApplicationProfile(this.profile.application);
          }
          this.getApplications();
        })
        .catch(err => {
          this.notify("negative", this.errorMessage(err, "Unable to submit evaluation."));
        })
        .finally(() => {
          this.savingAction = false;
        });
    },
    hasEvaluation(interviewId) {
      if (!this.profile.evaluations) return false;
      return this.profile.evaluations.some(e => e.interview_id === interviewId);
    },
    applicantName(row) {
      return [row.firstname, row.middlename, row.lastname].filter(Boolean).join(" ");
    },
    applicantPosition(row) {
      return row.position_applying_for === "OTHERS" ? row.other_position : row.position_applying_for || "N/A";
    },
    employeeName(row) {
      return row ? [row.firstname, row.lastname].filter(Boolean).join(" ") : "N/A";
    },
    normalizeLabel(value) {
      return String(value || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    },
    formatDate(value) {
      if (!value) return "N/A";
      return String(value).slice(0, 16).replace("T", " ");
    },
    formatMoney(value) {
      const amount = Number(value || 0);
      return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatSize(size) {
      if (!size) return "0 KB";
      return `${Math.round(size / 1024)} KB`;
    },
    vacancyStatusColor(status) {
      return { draft: "grey", published: "green", closed: "red", cancelled: "blue-grey" }[status] || "grey";
    },
    applicationStatusColor(status) {
      return {
        shortlisted: "green",
        approved_for_offer: "green",
        offer_accepted: "green",
        rejected: "red",
        offer_declined: "red",
        approval_pending: "orange",
        hold: "orange",
        onboarding: "primary"
      }[status] || "blue-grey";
    },
    offerStatusColor(status) {
      return { accepted: "green", declined: "red", sent: "primary", withdrawn: "grey" }[status] || "orange";
    },
    errorMessage(err, fallback) {
      return err && err.response && err.response.data && err.response.data.message ? err.response.data.message : fallback;
    }
  }
};
</script>

<style scoped>
.ats-header-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
</style>
